/**
 * WorkflowProgressView Component (Phase 19F: Multi-Step Workflow Automation)
 *
 * Displays multi-step workflow execution results with:
 * - Step indicator (1/3, 2/3, 3/3)
 * - Per-step status badge (pending/success/failed/cancelled)
 * - Summary on completion
 *
 * @module WorkflowProgressView
 */

'use client';

import React from 'react';
import { Box, Flex, Text } from '@axon/ui';
import { useTranslations } from 'next-intl';
import { LuCheck, LuX, LuClock, LuLoader, LuBan } from 'react-icons/lu';

import type { WorkflowData, WorkflowStepData } from '../types';

// ============================================================================
// Props
// ============================================================================

export interface WorkflowProgressViewProps {
  workflow: WorkflowData;
}

// ============================================================================
// Status helpers
// ============================================================================

function getStatusIcon(status: WorkflowStepData['status']): React.ReactNode {
  switch (status) {
    case 'success':
      return <LuCheck />;
    case 'failed':
      return <LuX />;
    case 'running':
      // Spinning loader so an in-progress step reads as active rather than static
      return <LuLoader className="animate-spin" />;
    case 'cancelled':
      return <LuBan />;
    case 'pending':
    default:
      return <LuClock />;
  }
}

/**
 * Semantic colorPalette per step status. The container sets `colorPalette`
 * and children reference `colorPalette.*` tokens (dark-mode correct).
 */
function getStatusPalette(status: WorkflowStepData['status']): string {
  switch (status) {
    case 'success':
      return 'green';
    case 'failed':
      return 'red';
    case 'running':
      return 'blue';
    case 'cancelled':
    case 'pending':
    default:
      return 'gray';
  }
}

// ============================================================================
// Component
// ============================================================================

export function WorkflowProgressView({ workflow }: WorkflowProgressViewProps) {
  const t = useTranslations('chatbot');
  return (
    <Box
      colorPalette={workflow.success ? 'green' : 'red'}
      borderRadius="md"
      borderWidth="1px"
      borderColor="colorPalette.emphasized"
      bg="colorPalette.subtle"
      padding="3"
      marginTop="2"
    >
      {/* Header */}
      <Flex justify="between" align="center" marginBottom="2">
        <Text fontWeight="semibold" fontSize="sm" color="colorPalette.fg">
          {t(workflow.success ? 'workflow.completed' : 'workflow.stopped')}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {t('workflow.stepsProgress', {
            completed: workflow.completedSteps,
            total: workflow.totalSteps,
          })}
        </Text>
      </Flex>

      {/* Steps — vertical stepper rail: a status disc per step connected by a
          line, so the sequence reads top-to-bottom at a glance. */}
      <Box>
        {workflow.steps.map((step, i) => {
          const isLast = i === workflow.steps.length - 1;
          return (
            <Flex
              key={step.index}
              colorPalette={getStatusPalette(step.status)}
              gap="2.5"
              align="stretch"
            >
              {/* Rail: status disc + connector to the next step */}
              <Flex direction="column" align="center" flexShrink={0}>
                <Flex
                  width="6"
                  height="6"
                  borderRadius="full"
                  background="colorPalette.solid"
                  color="colorPalette.contrast"
                  align="center"
                  justify="center"
                  fontSize="xs"
                  flexShrink={0}
                >
                  {getStatusIcon(step.status)}
                </Flex>
                {!isLast && (
                  <Box
                    width="2px"
                    flex="1"
                    minHeight="3"
                    background="border.emphasized"
                    marginY="1"
                  />
                )}
              </Flex>
              {/* Content */}
              <Box flex="1" paddingBottom={isLast ? '0' : '3'}>
                <Text fontSize="xs" fontWeight="medium" color="colorPalette.fg">
                  {t('workflow.stepLabel' as Parameters<typeof t>[0], { number: step.index + 1 })}:{' '}
                  {step.actionType} {step.entityType} {step.entityId}
                </Text>
                {step.resultMessage && (
                  <Text fontSize="xs" color="fg.muted" marginTop="0.5">
                    {step.resultMessage}
                  </Text>
                )}
              </Box>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
}
